import { useCallback, useMemo, useRef, useState } from 'react';
import { AdditionalInfoView, Priority } from '../../../types';
import { useAdditionalInfos } from '../../../hooks/use-additional-infos/additional-infos-provider';
import { usePinNarrative } from '../../../hooks/use-pin-narrative/use-pin-narrative';
import { usePinCap } from '../../../hooks/use-pin-narrative/use-pin-cap';

/**
 * Options that let each row parameterize the genuine divergences between
 * `AdditionalInfoRow` and `PinnedNarrativeRow`:
 *   1. `isPinnedRow` — which of the two instances this is. A pinned entry and
 *      its chronological twin share `info.id`; the hook derives edit /
 *      delete-confirmation from the store's single active-row action scoped by
 *      (id + row kind), so only the acting instance lights up.
 *   2. `shouldConfirmBeforeDelete` — whether delete opens the shared
 *      confirmation first, or deletes immediately (the legacy flag-off trash
 *      affordance on the chronological row).
 *   3. what happens after a delete succeeds / fails (`onDeleteSuccess` /
 *      `onDeleteError`).
 * Everything else (edit state, emphasize toggle + ref-guard, pin/unpin
 * passthrough, the in-flight delete state) is byte-identical and owned here.
 */
export interface UseNarrativeRowActionsOptions {
    /**
     * The row's info entity. `agencyEventId` is the event this narrative is
     * actually linked to (which for a merged family may differ from the panel's
     * entity) and scopes pin/unpin/delete to the correct link.
     */
    info: Pick<AdditionalInfoView, 'id' | 'priority' | 'agencyEventId'>;
    /**
     * Which of the two row instances this is: `true` for the pinned-section
     * row, `false` for the chronological-list row. Disambiguates the two rows
     * that share `info.id` so only the acting one shows the editor /
     * delete-confirmation.
     */
    isPinnedRow: boolean;
    /** Returns focus to the narrative input after edit/delete lifecycle events. */
    focusNarrativeInput?: () => void;
    /**
     * Whether clicking delete opens the shared confirmation first (flag-on rows
     * and all pinned rows) or deletes immediately (the legacy flag-off trash
     * affordance on the chronological row — pinned rows only exist when the flag
     * is on, so they always confirm).
     */
    shouldConfirmBeforeDelete: boolean;
    /**
     * Extra side effects after `deleteInfo` resolves successfully. The hook
     * already clears the active-row action; callers add only what diverges:
     *   - AdditionalInfoRow: toast + focus, but ONLY when the narrative-filtering
     *     flag is on.
     *   - PinnedNarrativeRow: always toast + focus.
     */
    onDeleteSuccess?: () => void;
    /**
     * Runs in the delete `catch` block. Each caller supplies its own error
     * surface (inline toast vs. showFailedToDeleteAlert).
     */
    onDeleteError?: () => void;
}

export interface UseNarrativeRowActionsResult {
    // edit
    isEditing: boolean;
    isSaving: boolean;
    handleEdit: () => void;
    handleEditCancel: () => void;
    handleEditSave: (updatedInfo: AdditionalInfoView) => Promise<void>;
    // emphasize
    isTogglingEmphasis: boolean;
    handleToggleEmphasis: () => Promise<void>;
    // delete
    isDeleting: boolean;
    isDeleteConfirmOpen: boolean;
    executeDelete: () => Promise<void>;
    handleDelete: () => void;
    handleDeleteCancel: () => void;
    // pin / unpin passthrough
    handlePin: () => Promise<void>;
    handleUnpin: () => Promise<void>;
    isPinning: boolean;
    // pin cap
    isPinCapReached: boolean;
    pinCapTooltip: string | undefined;
}

/**
 * Shared per-row action logic for the two narrative rows. Extracts the
 * ~90 lines of byte-identical action-handler wiring (edit, emphasize, delete,
 * pin/unpin) that previously lived in both `additional-info-row.tsx` and
 * `pinned-narrative-row.tsx`.
 *
 * Edit and delete-confirmation both derive from the store's single
 * `activeRowAction` ({ id, kind, isPinnedRow }). A row is "acting" only when the
 * action's id AND row kind match this instance — so pinning a narrative and
 * editing/deleting it no longer lights up both the pinned row and its
 * chronological twin.
 *
 * Returns a memoized bag of stable references so consumers can safely wire any
 * value into a downstream `useMemo`/`useEffect` dependency array.
 */
export function useNarrativeRowActions({
    info,
    isPinnedRow,
    focusNarrativeInput,
    shouldConfirmBeforeDelete,
    onDeleteSuccess,
    onDeleteError,
}: UseNarrativeRowActionsOptions): UseNarrativeRowActionsResult {
    const {
        deleteInfo,
        editInfo,
        emphasizeInfo,
        removeEmphasis,
        activeRowAction,
        setActiveRowAction,
        clearActiveRowAction,
    } = useAdditionalInfos();
    const { handlePin, handleUnpin, isPinning } = usePinNarrative(info.id, info.agencyEventId);
    const { isPinCapReached, pinCapTooltip } = usePinCap();

    // A pinned entry and its chronological twin share info.id, so match on BOTH
    // id and row kind — only the acting instance is live.
    const isActionOnThisRow =
        activeRowAction?.id === info.id && activeRowAction?.isPinnedRow === isPinnedRow;
    const isEditing = isActionOnThisRow && activeRowAction?.kind === 'edit';
    const isDeleteConfirmOpen = isActionOnThisRow && activeRowAction?.kind === 'delete-confirm';

    const [isSaving, setIsSaving] = useState(false);
    const [isTogglingEmphasis, setIsTogglingEmphasis] = useState(false);
    const isTogglingEmphasisRef = useRef(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleEdit = useCallback(
        () => setActiveRowAction({ id: info.id, kind: 'edit', isPinnedRow }),
        [info.id, isPinnedRow, setActiveRowAction]
    );

    const handleEditCancel = useCallback(() => {
        clearActiveRowAction();
        focusNarrativeInput?.();
    }, [clearActiveRowAction, focusNarrativeInput]);

    const handleEditSave = useCallback(
        async (updatedInfo: AdditionalInfoView) => {
            try {
                setIsSaving(true);
                await editInfo({ additionalInfoId: info.id, text: updatedInfo.text });
                clearActiveRowAction();
                focusNarrativeInput?.();
            } catch {
                // edit failed — keep edit mode open so user can retry
            } finally {
                setIsSaving(false);
            }
        },
        [editInfo, info.id, clearActiveRowAction, focusNarrativeInput]
    );

    const handleToggleEmphasis = useCallback(async () => {
        if (isTogglingEmphasisRef.current) {
            return;
        }
        try {
            isTogglingEmphasisRef.current = true;
            setIsTogglingEmphasis(true);
            await (info.priority === Priority.HIGH
                ? removeEmphasis(info.id)
                : emphasizeInfo(info.id));
        } catch {
            // toggle failed — silently ignore, matching the text-edit path
        } finally {
            isTogglingEmphasisRef.current = false;
            setIsTogglingEmphasis(false);
        }
    }, [info.priority, info.id, emphasizeInfo, removeEmphasis]);

    const executeDelete = useCallback(async () => {
        try {
            setIsDeleting(true);
            await deleteInfo(info.id, info.agencyEventId);
            // Close the confirmation (no-op on the legacy direct-delete path,
            // where no active action was ever set) before caller side effects.
            clearActiveRowAction();
            onDeleteSuccess?.();
        } catch {
            // Leave the confirmation open on failure so the user can retry.
            onDeleteError?.();
        } finally {
            setIsDeleting(false);
        }
    }, [
        deleteInfo,
        info.id,
        info.agencyEventId,
        clearActiveRowAction,
        onDeleteSuccess,
        onDeleteError,
    ]);

    const handleDelete = useCallback(() => {
        if (shouldConfirmBeforeDelete) {
            setActiveRowAction({ id: info.id, kind: 'delete-confirm', isPinnedRow });
        } else {
            executeDelete();
        }
    }, [shouldConfirmBeforeDelete, info.id, isPinnedRow, setActiveRowAction, executeDelete]);

    const handleDeleteCancel = useCallback(() => {
        clearActiveRowAction();
        focusNarrativeInput?.();
    }, [clearActiveRowAction, focusNarrativeInput]);

    return useMemo(
        () => ({
            isEditing,
            isSaving,
            handleEdit,
            handleEditCancel,
            handleEditSave,
            isTogglingEmphasis,
            handleToggleEmphasis,
            isDeleting,
            isDeleteConfirmOpen,
            executeDelete,
            handleDelete,
            handleDeleteCancel,
            handlePin,
            handleUnpin,
            isPinning,
            isPinCapReached,
            pinCapTooltip,
        }),
        [
            isEditing,
            isSaving,
            handleEdit,
            handleEditCancel,
            handleEditSave,
            isTogglingEmphasis,
            handleToggleEmphasis,
            isDeleting,
            isDeleteConfirmOpen,
            executeDelete,
            handleDelete,
            handleDeleteCancel,
            handlePin,
            handleUnpin,
            isPinning,
            isPinCapReached,
            pinCapTooltip,
        ]
    );
}
